import React, {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {GlobalContext} from "../../GlobalReducer";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {CustomerInterface} from "../../interfaces/customer/CustomerInterface";
import {CustomerManagerInterface} from "../../interfaces/customer/CustomerManagerInterface";
import {ChatDialogSummaryInterface, ChatFeedbackInterface, ChatMessageInterface, ChatRating as Rating} from "../../interfaces/customer/ChatInterface";
import {appendChatDialog, chatDialog, chatDialogs, deleteChatDialog, favoriteChatDialog, rateChatDialog, streamChat} from "../../api/chat";
import ChatMarkdown, {cardIds} from "./ChatMarkdown";
import ChatRating from "./ChatRating";

// Время в списке диалогов — рядом с номером в одну строку, поэтому коротко: «13.10 23:32», год — только не текущий
function dialogDate(iso: string): string {
    const date = new Date(iso);
    const pad = (value: number) => String(value).padStart(2, '0');
    const day = pad(date.getDate()) + '.' + pad(date.getMonth() + 1);

    return date.getFullYear() === new Date().getFullYear()
        ? day + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
        : day + '.' + date.getFullYear();
}

const MAX_HISTORY = 20; // сервер принимает до 40 сообщений
const MAX_INPUT_HEIGHT = 200; // px, примерно 8 строк

// Открытый диалог: id появляется после первого сохранения. На каждое переключение — новый объект,
// поэтому запоздавшее сохранение прошлого диалога не перепишет id открытого
interface OpenDialog {
    id: string | null
    saving: Promise<unknown>
}

// Как на сервере (AiChatDialogRepository::findListByOwner): последние сверху, избранные — внизу
function sortDialogs(dialogs: ChatDialogSummaryInterface[]): ChatDialogSummaryInterface[] {
    return [...dialogs].sort((a, b) => Number(!!a.favorite) - Number(!!b.favorite) || b.updated_at.localeCompare(a.updated_at));
}

// Сутки сообщения по местному времени — для разделителя «Сегодня», «Вчера», «9 октября 2026»
function dayOf(iso?: string): string {
    return iso ? new Date(iso).toDateString() : '';
}

function dayLabel(iso: string): string {
    const date = new Date(iso);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === new Date().toDateString()) return 'Сегодня';
    if (date.toDateString() === yesterday.toDateString()) return 'Вчера';

    return date.toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', year: 'numeric'}).replace(/\s*г\.$/, '');
}

function messageTime(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'});
}

// «Ответ за 0,8 с», «Ответ за 29 с»
function duration(ms: number): string {
    const seconds = ms / 1000;
    return 'Ответ за ' + (seconds < 10 ? seconds.toFixed(1).replace('.', ',') : Math.round(seconds)) + ' с';
}

// Плашка оценки — через 5 с после первого ответа в диалоге: не сразу, чтобы ответ успели прочитать
const RATE_DELAY_MS = 5000;
// Перезагрузка страницы позже — новый диалог: старый остаётся в списке, а новый вопрос не тянет за собой чужой контекст
const STALE_DIALOG_MS = 60 * 60 * 1000;

const NO_FEEDBACK: ChatFeedbackInterface = {rating: null, liked: '', disliked: ''};

const EXAMPLE_GROUPS = [
    {
        title: 'Вопросы по ассортименту',
        examples: [
            'какой вес у S03A8124?',
            'какие есть гайковерты с квадратом 1/2',
            'сравни JAI-1056 и AIW3416M',
            'что входит в набор S03A8109S?',
            'покажи похожие на S03A8124',
            'какая гарантия на JAI-1056?',
        ],
    },
    {
        title: 'Оснащение мастерской',
        examples: [
            'открываю СТО, нужен базовый инструмент',
            'подобрать тележку и верстак в автосервис',
            'какой набор купить в гараж?',
        ],
    },
    {
        title: 'Колёса, ходовая и тормоза',
        examples: [
            'что нужно, чтобы откручивать колёса грузовика?',
            'гайковерт для переобувки легковой',
            'стучит подвеска, надо поменять шаровую',
            'гудит подшипник ступицы, что нужно для замены?',
            'хрустит шрус при повороте',
            'подобрать инструмент для сайлентблоков',
            'что нужно для обслуживания суппорта?',
            'чем развальцевать тормозную трубку?',
        ],
    },
    {
        title: 'Двигатель и трансмиссия',
        examples: [
            'что нужно для замены ремня ГРМ?',
            'чем выкрутить свечи зажигания?',
            'сломалась свеча накаливания',
            'чем проверить компрессию?',
            'уходит антифриз, чем проверить?',
            'что нужно для замены масла в двигателе?',
            'чем открутить масляный фильтр?',
            'инструмент для замены сцепления',
        ],
    },
    {
        title: 'Крепёж, резьба и салон',
        examples: [
            'чем выкрутить сломанный болт?',
            'сорваны грани гайки, что подобрать?',
            'чем выкрутить целую шпильку?',
            'сорвал резьбу М8 в алюминии',
            'чем снять обшивку двери?',
        ],
    },
];

export default function Assistant() {
    const [state] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const customer = state.customer as CustomerInterface | null;
    const manager = state.manager as CustomerManagerInterface | null;

    if (!customer) {
        return <div className="alert alert-warning">Консультант доступен после входа в личный кабинет.</div>;
    }

    // Другой пользователь — другие диалоги: состояние страницы начинается заново
    return <AssistantChat key={customer.id + '-' + (manager?.id || 0)} customer={customer} manager={manager}/>;
}

function AssistantChat({customer, manager}: {customer: CustomerInterface, manager: CustomerManagerInterface | null}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogs, setDialogs] = useState<ChatDialogSummaryInterface[] | null>(null);
    const [dialogsError, setDialogsError] = useState('');
    const [messages, setMessages] = useState<ChatMessageInterface[]>([]);
    // Для каждого сообщения — товары, уже показанные карточками в ответах выше: второй раз только ссылкой
    const shownBefore = useMemo(() => {
        const shown = new Set<number>();
        return messages.map(message => {
            const before: ReadonlySet<number> = new Set(shown);
            if (message.role === 'assistant' && !message.error) {
                cardIds(message.content).forEach(id => shown.add(id));
            }
            return before;
        });
    }, [messages]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [progress, setProgress] = useState('');
    const [pending, setPending] = useState<string | null>(null); // ответ, который ещё идёт
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [input, setInput] = useState('');
    const [showDialogs, setShowDialogs] = useState(false);
    const [copied, setCopied] = useState(false);
    const [feedback, setFeedback] = useState<ChatFeedbackInterface>(NO_FEEDBACK);

    const current = useRef<OpenDialog>({id: null, saving: Promise.resolve()});
    const abort = useRef<AbortController | null>(null);
    const log = useRef<HTMLDivElement>(null);
    const textarea = useRef<HTMLTextAreaElement>(null);

    const setDialogInUrl = useCallback((id: string | null) => {
        setSearchParams(id ? {dialog: id} : {}, {replace: true});
    }, [setSearchParams]);

    // Поле ввода начинается с одной строки и растёт по мере набора — до MAX_INPUT_HEIGHT, дальше прокрутка
    useEffect(() => {
        const field = textarea.current;
        if (!field) return;
        field.style.height = 'auto';
        field.style.height = Math.min(field.scrollHeight + field.offsetHeight - field.clientHeight, MAX_INPUT_HEIGHT) + 'px';
        field.style.overflowY = field.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
    }, [input]);

    // Новый вопрос, ответ или открытый диалог — последний вопрос пользователя вверху ленты, ответ читается под ним с начала.
    // На прогресс и поток текста не реагируем, чтобы не дёргать читающего
    const waiting = pending !== null;
    useEffect(() => {
        const field = log.current;
        if (!field) return;
        const questions = field.querySelectorAll<HTMLElement>('.assistant__msg--user');
        const question = questions[questions.length - 1];
        field.scrollTop = question
            ? question.getBoundingClientRect().top - field.getBoundingClientRect().top + field.scrollTop - 12
            : field.scrollHeight;
    }, [messages.length, waiting]);

    // fresh — при загрузке страницы: диалог, где последнее сообщение старше часа, не открываем, начинаем новый
    const openDialog = useCallback((id: string, fresh = false) => {
        const opening: OpenDialog = {id, saving: Promise.resolve()};
        current.current = opening;
        setActiveId(id);
        setMessages([]);
        setFeedback(NO_FEEDBACK);
        setDialogInUrl(id);
        setShowDialogs(false);
        setLoading(true);
        chatDialog(id, customer, manager).then(dialog => {
            if (current.current !== opening) return;
            setLoading(false);
            const last = dialog.messages[dialog.messages.length - 1]?.created_at || dialog.updated_at;
            if (fresh && Date.now() - new Date(last).getTime() > STALE_DIALOG_MS) {
                current.current = {id: null, saving: Promise.resolve()};
                setActiveId(null);
                setDialogInUrl(null);
                return;
            }
            setFeedback({rating: dialog.rating || null, liked: dialog.feedback_liked || '', disliked: dialog.feedback_disliked || ''});
            setMessages(dialog.messages.map(message => ({
                role: message.role,
                content: message.content,
                at: message.created_at,
                ...(message.meta?.products ? {listed: message.meta.products} : {}),
                ...(message.meta?.context ? {context: message.meta.context} : {}),
                ...(message.meta?.timings?.total_ms ? {durationMs: message.meta.timings.total_ms} : {}),
            })));
        }).catch(error => {
            if (current.current !== opening) return;
            setLoading(false);
            current.current = {id: null, saving: Promise.resolve()};
            setActiveId(null);
            setDialogInUrl(null);
            setMessages([{role: 'assistant', content: 'Не удалось открыть диалог: ' + (error.response?.data?.message || error.message), error: true}]);
        });
    }, [customer, manager, setDialogInUrl]);

    const startDialog = () => {
        abort.current?.abort();
        current.current = {id: null, saving: Promise.resolve()};
        setActiveId(null);
        setMessages([]);
        setFeedback(NO_FEEDBACK);
        setPending(null);
        setLoading(false);
        setDialogInUrl(null);
        setShowDialogs(false);
        textarea.current?.focus();
    };

    useEffect(() => {
        chatDialogs(customer, manager)
            .then(body => setDialogs(sortDialogs(body.dialogs)))
            .catch(error => setDialogsError(error.response?.data?.message || error.message));

        // Диалог из адреса — по uid
        const initial = searchParams.get('dialog') || '';
        if (/^[0-9a-f]{10}$/.test(initial)) {
            openDialog(initial, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upsertDialog = (summary: ChatDialogSummaryInterface) => {
        setDialogs(list => sortDialogs([summary, ...(list || []).filter(dialog => dialog.id !== summary.id)]));
    };

    // Обмен — вопрос и ответ — в историю диалога; первый обмен заводит диалог.
    // Сохранения одного диалога идут по очереди, иначе два первых обмена завели бы два диалога
    const persist = (dialog: OpenDialog, exchange: ChatMessageInterface[]) => {
        dialog.saving = dialog.saving
            .then(() => appendChatDialog(dialog.id, exchange, customer, manager))
            .then(summary => {
                dialog.id = summary.id;
                if (current.current === dialog) {
                    setActiveId(summary.id);
                    setDialogInUrl(summary.id);
                }
                upsertDialog(summary);
            })
            .catch(() => {
                // Ответ на экране остаётся; в истории его не будет
            });
    };

    const ask = async (text: string) => {
        const dialog = current.current;
        const question: ChatMessageInterface = {role: 'user', content: text, at: new Date().toISOString()};
        const history = [...messages.filter(message => !message.error), question];
        setMessages(list => [...list, question]);
        setPending('');
        setProgress('Изучаю ваш запрос…');
        setBusy(true);

        const controller = new AbortController();
        abort.current = controller;
        let reply = '';
        let failed = '';
        let listed: ChatMessageInterface['listed'];
        let replyId = '';
        let context = '';
        // Время ответа: по серверу (событие timings), без него — от отправки вопроса
        const started = Date.now();
        let durationMs = 0;

        try {
            await streamChat(history.slice(-MAX_HISTORY), customer, manager, event => {
                if (event.type === 'progress') {
                    if (current.current === dialog) setProgress(event.text);
                } else if (event.type === 'delta') {
                    reply += event.text;
                    if (current.current === dialog) setPending(reply);
                } else if (event.type === 'error') {
                    failed = event.message;
                } else if (event.type === 'products') {
                    listed = {ids: event.ids, total: event.total};
                } else if (event.type === 'start') {
                    replyId = event.reply_id || '';
                } else if (event.type === 'context') {
                    context = event.text;
                } else if (event.type === 'timings') {
                    durationMs = event.total_ms;
                }
            }, controller.signal);
            if (!reply) {
                throw new Error(failed || 'Пустой ответ.');
            }
        } catch (error: any) {
            if (!reply) {
                if (current.current === dialog) {
                    // Вопрос без ответа в историю не идёт: иначе следующий запрос уйдёт с двумя вопросами подряд
                    setMessages(list => [
                        ...list.slice(0, -1),
                        {...question, error: true},
                        {role: 'assistant', content: error.name === 'AbortError' ? 'Остановлено.' : error.message, error: true, at: new Date().toISOString()},
                    ]);
                    setPending(null);
                }
                abort.current = null;
                setBusy(false);
                return;
            }
        }

        const answer: ChatMessageInterface = {
            role: 'assistant',
            content: reply,
            at: new Date().toISOString(),
            ...(listed ? {listed} : {}),
            ...(replyId ? {replyId} : {}),
            ...(context ? {context} : {}),
            durationMs: durationMs || Date.now() - started,
        };
        if (current.current === dialog) {
            setMessages(list => [...list, answer]);
            setPending(null);
        }
        persist(dialog, [question, answer]);
        abort.current = null;
        setBusy(false);
    };

    const submit = (event?: React.FormEvent) => {
        event?.preventDefault();
        const text = input.trim();
        if (!text || busy) return;
        setInput('');
        ask(text);
    };

    // Без подтверждения: диалог только помечается удалённым (в админке он остаётся)
    const remove = (id: string) => {
        deleteChatDialog(id, customer, manager).then(() => {
            setDialogs(list => (list || []).filter(dialog => dialog.id !== id));
            if (current.current.id === id) startDialog();
        }).catch(error => setDialogsError('Не удалось удалить: ' + (error.response?.data?.message || error.message)));
    };

    // Оценка — сразу на странице, при ошибке — обратно
    const rate = (rating: Rating) => {
        const dialogId = current.current.id;
        if (!dialogId || rating === feedback.rating) return;
        const previous = feedback.rating;
        setFeedback(value => ({...value, rating}));
        rateChatDialog(dialogId, {rating}, customer, manager).catch(() => {
            if (current.current.id === dialogId) setFeedback(value => ({...value, rating: previous}));
        });
    };

    const comment = (liked: string, disliked: string) => {
        const dialogId = current.current.id;
        if (!dialogId) return Promise.reject(new Error('Диалог ещё не сохранён.'));

        return rateChatDialog(dialogId, {feedback: {liked, disliked}}, customer, manager).then(() => {
            if (current.current.id === dialogId) setFeedback(value => ({...value, liked, disliked}));
        });
    };

    // Уже оценённый диалог — плашка сразу; иначе — через RATE_DELAY_MS после первого ответа (для открытого из списка — после открытия)
    const answered = messages.some(message => message.role === 'assistant' && !message.error);
    const [ratingReady, setRatingReady] = useState(false);
    useEffect(() => {
        setRatingReady(false);
        if (!activeId || !answered) return;
        const timer = setTimeout(() => setRatingReady(true), RATE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [activeId, answered]);
    const showRating = !!activeId && answered && (ratingReady || !!feedback.rating);
    // Плашка появилась у низа ленты — докручиваем, чтобы её было видно; читающего выше не дёргаем
    useEffect(() => {
        const field = log.current;
        if (showRating && field && field.scrollHeight - field.scrollTop - field.clientHeight < 160) {
            field.scrollTop = field.scrollHeight;
        }
    }, [showRating]);

    const toggleFavorite = (dialog: ChatDialogSummaryInterface) => {
        const favorite = !dialog.favorite;
        const mark = (value: boolean) => setDialogs(list => sortDialogs((list || []).map(item => item.id === dialog.id ? {...item, favorite: value} : item)));
        mark(favorite);
        favoriteChatDialog(dialog.id, favorite, customer, manager).catch(error => {
            mark(!favorite);
            setDialogsError('Не удалось изменить избранное: ' + (error.response?.data?.message || error.message));
        });
    };

    const dialogList = (
        <div className="assistant__dialogs">
            {dialogsError && <div className="text-danger small p-3">{dialogsError}</div>}
            {dialogs === null && !dialogsError && <div className="text-muted small p-3">Загрузка…</div>}
            {dialogs !== null && dialogs.length === 0 && (
                <div className="text-muted small p-3">Диалогов пока нет: задайте вопрос — диалог сохранится здесь.</div>
            )}
            {dialogs?.map((dialog, index) => (
                <Fragment key={dialog.id}>
                {dialog.favorite && (index === 0 || !dialogs[index - 1].favorite) && (
                    <div className="assistant__dialogs-group">Избранное</div>
                )}
                <div
                     className={'assistant__dialog' + (dialog.id === activeId ? ' assistant__dialog--active' : '') + (busy ? ' assistant__dialog--disabled' : '')}
                     title={dialog.title}
                     onClick={() => !busy && dialog.id !== activeId && openDialog(dialog.id)}>
                    <div className="assistant__dialog-body">
                        <div className="assistant__dialog-title">{dialog.title}</div>
                        {/* Номер — тот, что называют в замечаниях («в диалоге #630b616844 …») */}
                        <div className="assistant__dialog-date"><span className="assistant__dialog-uid">#{dialog.id}</span>{dialogDate(dialog.updated_at)}</div>
                    </div>
                    <div className="assistant__dialog-actions">
                        <button type="button"
                                className={'assistant__dialog-action assistant__dialog-favorite' + (dialog.favorite ? ' assistant__dialog-favorite--on' : '')}
                                title={dialog.favorite ? 'Убрать из избранного' : 'В избранное'}
                                disabled={busy}
                                onClick={event => {
                                    event.stopPropagation();
                                    toggleFavorite(dialog);
                                }}>
                            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                                <path d="M12 21s-7.5-4.6-9.5-9.2C1.1 8.4 3.2 4.5 7 4.5c2.1 0 3.6 1.1 5 2.9 1.4-1.8 2.9-2.9 5-2.9 3.8 0 5.9 3.9 4.5 7.3C19.5 16.4 12 21 12 21z"
                                      fill={dialog.favorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <button type="button"
                                className="assistant__dialog-action assistant__dialog-delete"
                                title="Удалить диалог"
                                disabled={busy}
                                onClick={event => {
                                    event.stopPropagation();
                                    remove(dialog.id);
                                }}>
                            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
                </Fragment>
            ))}
        </div>
    );

    // Номер диалога (uid в prs_ai_chat_dialog) — чтобы сослаться на диалог в замечании; по клику копируется
    const copyDialogId = () => {
        navigator.clipboard?.writeText(activeId || '').then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
    };

    return (
        <div className="assistant">
            <div className="d-flex align-items-center gap-2 mb-3">
                <h3 className="mb-0">Консультант по товарам</h3>
                <span className="me-auto">
                    {activeId && <button type="button" className="assistant__dialog-id" title="Скопировать номер диалога" onClick={copyDialogId}>
                        {copied ? 'Скопировано' : 'Диалог #' + activeId}
                    </button>}
                </span>
                <button type="button" className="btn gray assistant__tool d-lg-none" onClick={() => setShowDialogs(!showDialogs)}>
                    Диалоги{dialogs?.length ? ' (' + dialogs.length + ')' : ''}
                </button>
                <button type="button" className="btn btn-primary assistant__tool" onClick={startDialog}>
                    Новый диалог
                </button>
            </div>

            <div className="assistant__layout">
                <div className={'assistant__sidebar' + (showDialogs ? ' assistant__sidebar--open' : '')}>
                    {dialogList}
                </div>

                <div className="assistant__chat">
                    <div className="assistant__log" ref={log}>
                        {loading && <div className="text-muted small">Загрузка диалога…</div>}
                        {!loading && messages.length === 0 && pending === null && (
                            <div className="assistant__empty">
                                <p>Спросите о товарах Jonnesway, Ombra, Thorvik, Carbontools и FillInn или опишите задачу — консультант поможет подобрать инструмент.</p>
                                {EXAMPLE_GROUPS.map(group => (
                                    <details className="assistant__examples" key={group.title}>
                                        <summary>{group.title}</summary>
                                        <ul>
                                            {group.examples.map(example => (
                                                <li key={example}>
                                                    <a href="#assistant-question" className="assistant__example" onClick={event => {
                                                        event.preventDefault();
                                                        if (!busy) ask(example);
                                                    }}>{example}</a>
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                ))}
                            </div>
                        )}
                        {messages.map((message, index) => (
                            <Fragment key={index}>
                                {message.at && dayOf(message.at) !== dayOf(messages.slice(0, index).reverse().find(item => item.at)?.at) && (
                                    <div className="assistant__day"><span>{dayLabel(message.at)}</span></div>
                                )}
                                <div className={'assistant__msg assistant__msg--' + message.role + (message.error ? ' assistant__msg--error' : '')}>
                                    <div className="assistant__bubble">
                                        {message.role === 'user' ? message.content : <ChatMarkdown text={message.content} withCards={!message.error} shownBefore={shownBefore[index]} listed={message.listed}
                                                                                              onChoose={index === messages.length - 1 && !busy && !message.error ? ask : undefined}/>}
                                    </div>
                                    {message.role === 'user' && message.at && <div className="assistant__time">{messageTime(message.at)}</div>}
                                    {message.role === 'assistant' && !message.error && message.durationMs ? <div className="assistant__time">{duration(message.durationMs)}</div> : null}
                                </div>
                            </Fragment>
                        ))}
                        {pending !== null && (
                            <div className="assistant__msg assistant__msg--assistant">
                                <div className="assistant__bubble">
                                    {pending ? <ChatMarkdown text={pending}/> : <span className="text-muted fst-italic" role="status" aria-live="polite">{progress || 'Обрабатываем запрос'}<span className="assistant__dots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span></span>}
                                </div>
                            </div>
                        )}
                        {showRating && <ChatRating feedback={feedback} onRate={rate} onComment={comment}/>}
                    </div>

                    <form className="assistant__form" onSubmit={submit}>
                        <textarea ref={textarea} id="assistant-question"
                                  className="form-control"
                                  rows={1}
                                  maxLength={4000}
                                  value={input}
                                  placeholder="Вопрос о товаре… (Enter — отправить, Shift+Enter — новая строка)"
                                  onChange={event => setInput(event.target.value)}
                                  onKeyDown={event => {
                                      if (event.key === 'Enter' && !event.shiftKey) {
                                          event.preventDefault();
                                          submit();
                                      }
                                  }}/>
                        {busy
                            ? <button type="button" className="btn btn-secondary two" onClick={() => abort.current?.abort()}>Стоп</button>
                            : <button type="submit" className="btn btn-primary two" disabled={!input.trim()}>Отправить</button>}
                    </form>
                    <div className="assistant__disclaimer">
                        Консультант — нейросеть и может ошибаться. Важные вопросы уточняйте у персонального менеджера.
                    </div>
                </div>
            </div>
        </div>
    );
}
